import {AuthProviderType} from "../../domain/providers";
import React, {FC} from "react";
import {useSsoSignIn} from "../sso/SsoButtons";
import {useNavigate} from "react-router";
import {UserAvatar} from "./UserAvatar";
import {Badge, Icon, Text} from "@wakelet/ui-kit";
import {toProviderIconComponent} from "../sso/icons";
import {translateKey} from "../language/translate";

type UserAccountListItemProps = {
  account?: number,
  email: string,
  displayName: string,
  provider: AuthProviderType,
  photoURL?: string,
  signedIn?: boolean
  onDelete?: () => void,
  disabled?: boolean
}

export const UserAccountListItem: FC<UserAccountListItemProps> = ({
  account,
  displayName,
  email,
  photoURL,
  provider,
  signedIn,
  onDelete,
  disabled
}) => {
  const {signInWith} = useSsoSignIn("login");
  const navigate = useNavigate();
  const userIcon = <UserAvatar displayName={displayName} email={email} photoURL={photoURL}/>;
  const signedInIcon = <Icon id="circledTick"/>;
  const body = (
    <div className="relative flex flex-grow flex-col w-full">
      <div className="flex flex-row w-full items-center">
        <Text.Paragraph className="sa-name truncate">{displayName}</Text.Paragraph>
        <div className="flex flex-grow"></div>
        {signedIn &&
          <Badge variant="primary" text={translateKey("account:switchAccount.labels.signedIn")} icon={signedInIcon}/>}
        {onDelete && <DeleteUserAccountButton onClick={onDelete}/>}
      </div>
      <Text.Style className="truncate" as="caption"><p>{email}</p></Text.Style>
    </div>)
  const providerIcon = <ProviderIcon icon={getProviderIcon(provider)}/>
  let onClick = provider === "password" ? () => navigate(`/login?account=${account}`) : () => signInWith(provider);
  if (onDelete) {
    onClick = () => {
      return;
    }
  }
  return <AccountListItem
    id={email}
    icon={userIcon}
    body={body}
    onClick={onClick}
    providerIcon={providerIcon}
    disabled={disabled}/>
};

export const getProviderIcon = (provider: AuthProviderType) => {
  const Component = toProviderIconComponent(provider)
  return <Component size={getSize(provider)}/>
}

const getSize = (provider: AuthProviderType) => {
  if (provider === AuthProviderType.Facebook || provider === AuthProviderType.Microsoft) {
    return 12;
  }
  return 14;
}

export const ProviderIcon = ({icon, testId}: { icon: React.ReactElement, testId?: string }) => {
  return <div className="w-5 h-5 p-1 bg-white rounded-full flex justify-center items-center" data-testid={testId}>
    <span>{icon}</span>
  </div>
}

export const DeleteUserAccountButton: FC<{ onClick: () => void }> = ({onClick}) => {
  return <button
    className="rounded-full border border-black h-fit hover:cursor-pointer"
    data-testid="delete-account-btn"
    onClick={onClick}>
    <Icon id="close"/>
  </button>;
};

type AccountListItemProps = {
  id: string;
  icon: React.ReactElement;
  providerIcon: React.ReactElement;
  body: React.ReactElement;
  onClick: () => void;
  disabled?: boolean;
}

const AccountListItem = ({id, icon, body, onClick, providerIcon, disabled}: AccountListItemProps) => {
  const disabledClass = disabled ? "opacity-50 disabled" : "";
  return (
    <div
      className={`flex flex-row hover:bg-gray-50 hover:cursor-pointer rounded-2xl p-2 ${disabledClass}`}
      onClick={() => !disabled && onClick()}
      data-testid={`account-list-item-${id}`}>
      <div className="flex items-center justify-center w-10 mr-2">
        <div className="w-full relative">
          {icon}
          <span className="absolute top-6 left-6">
                {providerIcon}
              </span>
        </div>
      </div>
      <div className="sa-info flex flex-grow items-center w-full">
        {body}
      </div>
    </div>);
};
